import { useCallback, useEffect, useRef, useState } from 'react';
import type { BorrowerType } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type { TIncomeStructureByKvedFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import type {
  StateAidCertificateType,
  TStateAidFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import { EMPTY_DOCUMENT_GENERATION_VALIDATION_ISSUES } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/constants.ts';
import type {
  TCertificateSaveResult,
  TDocumentGenerationOptions,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import type { TApiApplicationDocument } from '~/modules/covenants/types.ts';
import {
  buildDocumentGenerationOptions,
  getCertificateDocumentFlags,
  getDocumentGenerationSaveTooltip,
  getIsDocumentGenerationDisabled,
  isSuccessfulCertificateSave,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/utils.ts';
import {
  focusDocumentGenerationField,
  type TDocumentGenerationValidationIssues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/validation.ts';

type TUseDocumentGenerationModalStateParams = {
  open: boolean;
  documentId: string | number;
  isGenerating: boolean;
  stateAidCertificateType?: StateAidCertificateType;
  regenerationSource?: TApiApplicationDocument;
  onClose: () => void;
  onGenerate: (options: TDocumentGenerationOptions) => Promise<boolean>;
  onSaveFinancialCondition: (
    values: TFinancialConditionFormValues,
    version: number
  ) => Promise<TCertificateSaveResult>;
  onSaveIncomeStructureByKved: (
    values: TIncomeStructureByKvedFormValues,
    version: number
  ) => Promise<TCertificateSaveResult>;
  onSaveStateAidReceived: (
    type: StateAidCertificateType,
    values: TStateAidFormValues,
    version: number
  ) => Promise<TCertificateSaveResult>;
};

export const useDocumentGenerationModalState = ({
  open,
  documentId,
  isGenerating,
  stateAidCertificateType,
  regenerationSource,
  onClose,
  onGenerate,
  onSaveFinancialCondition,
  onSaveIncomeStructureByKved,
  onSaveStateAidReceived,
}: TUseDocumentGenerationModalStateParams) => {
  const documentFlags = getCertificateDocumentFlags(documentId);
  const isSaveableCertificate = Object.values(documentFlags).some(Boolean);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [borrowerType, setBorrowerType] = useState<BorrowerType | null>(null);
  const [pendingHistoricalFieldNames, setPendingHistoricalFieldNames] = useState<string[]>([]);
  const [validationIssues, setValidationIssues] = useState<TDocumentGenerationValidationIssues>(
    EMPTY_DOCUMENT_GENERATION_VALIDATION_ISSUES
  );
  const saveAttemptRef = useRef<(() => void) | null>(null);
  const isBusy = isGenerating || isSaving;
  const saveTooltip = getDocumentGenerationSaveTooltip({
    pendingHistoricalFieldNames,
    validationIssues,
  });
  const isGenerateDisabled = getIsDocumentGenerationDisabled({
    isBusy,
    hasPendingHistoricalFields: pendingHistoricalFieldNames.length > 0,
    isSaveableCertificate,
    isSaved,
    isFinancialCondition: documentFlags.isFinancialCondition,
    hasBorrowerType: Boolean(borrowerType),
    isStateAidReceived: documentFlags.isStateAidReceived,
    hasStateAidCertificateType: Boolean(stateAidCertificateType),
  });

  useEffect(() => {
    if (open) {
      return;
    }

    setIsSaved(false);
    setIsFormDirty(false);
    setBorrowerType(null);
    setPendingHistoricalFieldNames([]);
    setValidationIssues(EMPTY_DOCUMENT_GENERATION_VALIDATION_ISSUES);
    saveAttemptRef.current = null;
  }, [open]);

  const runSave = useCallback(
    async (save: () => Promise<TCertificateSaveResult>): Promise<TCertificateSaveResult> => {
      setIsSaving(true);

      try {
        const result = await save();

        if (isSuccessfulCertificateSave(result)) {
          setIsSaved(true);
        }

        if (result === 'versionMismatch') {
          onClose();
        }

        return result;
      } finally {
        setIsSaving(false);
      }
    },
    [onClose]
  );

  const handleFormChange = useCallback((isDirty: boolean) => {
    setIsFormDirty(isDirty);

    if (isDirty) {
      setIsSaved(false);
    }
  }, []);

  const handleSaveAttemptChange = useCallback((attemptSave: (() => void) | null) => {
    saveAttemptRef.current = attemptSave;
  }, []);

  const handleSaveClick = useCallback(() => {
    const firstPendingFieldName = pendingHistoricalFieldNames[0];

    if (firstPendingFieldName) {
      focusDocumentGenerationField(firstPendingFieldName);
      return;
    }

    saveAttemptRef.current?.();
  }, [pendingHistoricalFieldNames]);

  const handleGenerate = useCallback(() => {
    void onGenerate(
      buildDocumentGenerationOptions({
        documentId,
        borrowerType,
        stateAidCertificateType,
        regenerationSource,
      })
    );
  }, [borrowerType, documentId, onGenerate, regenerationSource, stateAidCertificateType]);

  return {
    ...documentFlags,
    isBusy,
    isFormDirty,
    isGenerateDisabled,
    isSaveableCertificate,
    isSaving,
    saveTooltip,
    handleBorrowerTypeChange: setBorrowerType,
    handleFormChange,
    handleGenerate,
    handleSaveClick,
    handleSaveAttemptChange,
    handleSaveFinancialCondition: (values: TFinancialConditionFormValues, version: number) =>
      runSave(() => onSaveFinancialCondition(values, version)),
    handleSaveIncomeStructureByKved: (values: TIncomeStructureByKvedFormValues, version: number) =>
      runSave(() => onSaveIncomeStructureByKved(values, version)),
    handleSaveStateAidReceived: (
      type: StateAidCertificateType,
      values: TStateAidFormValues,
      version: number
    ) => runSave(() => onSaveStateAidReceived(type, values, version)),
    setPendingHistoricalFieldNames,
    setValidationIssues,
  };
};
